import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

// Dropdown for @mention typeahead. Local state only (no MobX). The host owns
// the textarea and keyboard events; it calls handleKey(key) via ref and renders
// this component only while a mention query is active. Results are club-scoped
// by the backend (employees endpoint).
class MentionTypeahead extends Component {
    constructor(props) {
        super(props);
        this.state = {
            results: [],
            selectedIdx: 0,
            loading: true,
        };
        this.searchTimer = null;
    }

    componentDidMount() {
        this.search(this.props.query);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.query !== this.props.query) {
            this.search(this.props.query);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
    }

    search = (query) => {
        clearTimeout(this.searchTimer);
        const term = (query || '').trim();
        if (term.length < 1) {
            this.setState({ results: [], selectedIdx: 0, loading: false });
            return;
        }
        this.searchTimer = setTimeout(async () => {
            try {
                // /api/users/mentions/ (not /employees/): club members are in the
                // domain customer's members[] pool, not user.customer - see the
                // backend route. hostname keeps the same feed scoping the
                // composer itself uses.
                const { articleStore } = this.props.stores;
                const response = await util.fetchApi('/api/users/mentions/', { method: 'GET' }, {
                    search: term,
                    limit: 8,
                    hostname: articleStore.currentHostname || '',
                });
                if ((this.props.query || '').trim() !== term) {
                    return; // stale response — a newer query is in flight
                }
                const { userStore } = this.props.stores;
                const currentUserId = userStore.user?.id;
                const results = (response?.data || []).filter(u => u.uuidv4 && u.id !== currentUserId);
                this.setState({ results, selectedIdx: 0, loading: false });
            } catch (err) {
                if ((this.props.query || '').trim() !== term) {
                    return; // stale response — a newer query is in flight
                }
                this.setState({ results: [], selectedIdx: 0, loading: false });
            }
        }, 300);
    }

    // Called by the host's onKeyDown. Returns true when the key was consumed.
    handleKey = (key) => {
        const { results, selectedIdx } = this.state;
        if (key === 'Escape') {
            this.props.onClose();
            return true;
        }
        if (results.length === 0) {
            return false;
        }
        if (key === 'ArrowDown') {
            this.setState({ selectedIdx: (selectedIdx + 1) % results.length });
            return true;
        }
        if (key === 'ArrowUp') {
            this.setState({ selectedIdx: (selectedIdx - 1 + results.length) % results.length });
            return true;
        }
        if (key === 'Enter' || key === 'Tab') {
            this.pick(results[selectedIdx]);
            return true;
        }
        return false;
    }

    pick = (user) => {
        this.props.onSelect({
            id: user.id,
            uuidv4: user.uuidv4,
            name: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
        });
    }

    render() {
        const { results, selectedIdx, loading } = this.state;
        const { posStyle } = this.props;
        return (
            <div
                class='position-absolute list-group shadow'
                style={`z-index: 1060; max-height: 40vh; overflow-y: auto; min-width: 250px; ${posStyle || 'top: 100%; left: 0;'}`}
            >
                {results.map((user, idx) => (
                    <button
                        type='button'
                        key={user.id}
                        class={`list-group-item list-group-item-action py-2 text-start ${idx === selectedIdx ? 'active' : ''}`}
                        onMouseDown={e => {
                            e.preventDefault(); // keep textarea focus until pick() runs
                            this.pick(user);
                        }}
                    >
                        <div class='fw-semibold'>{`${user.firstname || ''} ${user.lastname || ''}`.trim()}</div>
                        <small class={idx === selectedIdx ? '' : 'text-body-secondary'}>{user.email}</small>
                    </button>
                ))}
                {!loading && results.length === 0 && (
                    <div class='list-group-item text-body-secondary small'>
                        <Text id='mentions.no-members'>No members found</Text>
                    </div>
                )}
            </div>
        );
    }
}

export default MentionTypeahead;
